'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import EmergencyFundModal from './EmergencyFundModal';

interface EmergencyProject {
  id: number;
  title: string;
  location: string;
  crisis: string;
  urgency: 'critical' | 'urgent' | 'active';
  targetAmount: number;
  raisedAmount: number;
  beneficiaries: number;
  timeRemaining: string;
  image: string;
  alt: string;
  description: string;
}

const EmergencyResponseSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<EmergencyProject | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const emergencyProjects: EmergencyProject[] = [
  {
    id: 1,
    title: 'Earthquake Relief - Turkey & Syria',
    location: 'Hatay Province, Turkey',
    crisis: 'Natural Disaster',
    urgency: 'critical',
    targetAmount: 500000,
    raisedAmount: 387500,
    beneficiaries: 15000,
    timeRemaining: '48 hours',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a913b5a4-1767964619604.png",
    alt: 'Emergency response team distributing supplies to earthquake survivors in temporary shelter',
    description: 'Immediate shelter, food, and medical supplies for families displaced by devastating earthquake'
  },
  {
    id: 2,
    title: 'Flood Emergency Response',
    location: 'Sindh Province, Pakistan',
    crisis: 'Climate Disaster',
    urgency: 'urgent',
    targetAmount: 350000,
    raisedAmount: 245000,
    beneficiaries: 12000,
    timeRemaining: '5 days',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d885e9f1-1767386171307.png",
    alt: 'Volunteers delivering emergency food packages to flood-affected families in boats',
    description: 'Clean water, emergency food, and disease prevention for communities affected by severe flooding'
  },
  {
    id: 3,
    title: 'Conflict Zone Medical Aid',
    location: 'Gaza Strip, Palestine',
    crisis: 'Humanitarian Crisis',
    urgency: 'critical',
    targetAmount: 750000,
    raisedAmount: 612000,
    beneficiaries: 25000,
    timeRemaining: '72 hours',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5356d62-1767809612510.png",
    alt: 'Medical professionals treating injured civilians in emergency field hospital',
    description: 'Critical medical supplies, trauma care, and emergency surgery for conflict-affected populations'
  }];


  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical':
        return 'bg-error text-error-foreground';
      case 'urgent':
        return 'bg-warning text-warning-foreground';
      case 'active':
        return 'bg-primary text-primary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const calculateProgress = (raised: number, target: number) => {
    return Math.min(raised / target * 100, 100);
  };

  if (!isHydrated) {
    return (
      <section className="py-20 bg-gradient-to-br from-error/5 to-warning/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
              Emergency Response Fund
            </h2>
            <p className="text-lg text-muted-foreground font-body">Loading emergency projects...</p>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="py-20 bg-gradient-to-br from-error/5 to-warning/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-error/20 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
            <Icon name="ExclamationTriangleIcon" size={24} className="text-error animate-pulse" variant="solid" />
            <span className="text-error font-cta font-bold text-sm">
              URGENT: Active Emergency Responses
            </span>
          </div>
          
          <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
            Emergency Response Fund
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            When disaster strikes, every second counts. Your rapid response donation provides immediate life-saving support to communities in crisis.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {emergencyProjects.map((project) =>
          <div
            key={project.id}
            className="bg-card rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-error/30">

              <div className="relative h-56 overflow-hidden">
                <AppImage
                src={project.image}
                alt={project.alt}
                className="w-full h-full object-cover" />

                <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-xs font-cta font-bold ${getUrgencyColor(project.urgency)} animate-pulse`}>
                    {project.urgency.toUpperCase()}
                  </span>
                  <span className="px-3 py-1 bg-foreground/80 backdrop-blur-sm text-background rounded-full text-xs font-cta font-bold">
                    {project.timeRemaining} left
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="MapPinIcon" size={16} className="text-error" variant="solid" />
                  <span className="text-sm font-body text-muted-foreground">
                    {project.location}
                  </span>
                </div>

                <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                  {project.title}
                </h3>

                <p className="text-sm text-muted-foreground font-body mb-4 leading-relaxed">
                  {project.description}
                </p>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground font-body">Target</span>
                    <span className="font-cta font-bold text-foreground">
                      ${project.targetAmount.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground font-body">Raised</span>
                    <span className="font-cta font-bold text-success">
                      ${project.raisedAmount.toLocaleString()}
                    </span>
                  </div>

                  <div>
                    <div className="w-full bg-muted rounded-full h-3 overflow-hidden mb-2">
                      <div
                      className="bg-gradient-to-r from-error to-warning h-full rounded-full transition-all duration-500"
                      style={{ width: `${calculateProgress(project.raisedAmount, project.targetAmount)}%` }} />

                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground font-body">
                        {calculateProgress(project.raisedAmount, project.targetAmount).toFixed(1)}% funded
                      </span>
                      <span className="text-muted-foreground font-body">
                        {project.beneficiaries.toLocaleString()} people
                      </span>
                    </div>
                  </div>
                </div>

                <button
                onClick={() => {
                  setSelectedProject(project);
                  setIsModalOpen(true);
                }}
                className="w-full px-4 py-3 bg-error text-error-foreground rounded-lg font-cta font-bold text-sm transition-all duration-300 hover:scale-105 hover:shadow-lg pulse-gentle flex items-center justify-center space-x-2">
                
                  <Icon name="BoltIcon" size={18} variant="solid" />
                  <span>Donate Now</span>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-card rounded-xl p-8 shadow-lg border-2 border-error/30">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-headline font-bold text-foreground mb-4">
                How Emergency Response Works
              </h3>
              <p className="text-lg text-muted-foreground font-body mb-6">
                Our rapid deployment system ensures your emergency donation reaches those in need within 24-48 hours of crisis onset.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-error/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-error font-cta font-bold text-sm">1</span>
                  </div>
                  <div>
                    <div className="font-cta font-bold text-foreground mb-1">
                      Crisis Assessment
                    </div>
                    <p className="text-sm text-muted-foreground font-body">
                      Real-time monitoring identifies urgent humanitarian needs
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-error/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-error font-cta font-bold text-sm">2</span>
                  </div>
                  <div>
                    <div className="font-cta font-bold text-foreground mb-1">
                      Rapid Mobilization
                    </div>
                    <p className="text-sm text-muted-foreground font-body">
                      Pre-positioned supplies and trained teams deploy immediately
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-error/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-error font-cta font-bold text-sm">3</span>
                  </div>
                  <div>
                    <div className="font-cta font-bold text-foreground mb-1">
                      Direct Delivery
                    </div>
                    <p className="text-sm text-muted-foreground font-body">
                      Life-saving aid reaches affected communities within 48 hours
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-error/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-error font-cta font-bold text-sm">4</span>
                  </div>
                  <div>
                    <div className="font-cta font-bold text-foreground mb-1">
                      Real-Time Updates
                    </div>
                    <p className="text-sm text-muted-foreground font-body">
                      Donors receive immediate impact reports and photo documentation
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-error/10 to-warning/10 rounded-xl p-8 border border-error/20">
              <h4 className="text-2xl font-headline font-bold text-foreground mb-6 text-center">
                Emergency Fund Impact
              </h4>
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-5xl font-headline font-bold text-error mb-2">
                    24-48h
                  </div>
                  <p className="text-sm text-muted-foreground font-body">
                    Average deployment time from crisis to aid delivery
                  </p>
                </div>
                <div className="h-px bg-border" />
                <div className="text-center">
                  <div className="text-5xl font-headline font-bold text-warning mb-2">
                    127K+
                  </div>
                  <p className="text-sm text-muted-foreground font-body">
                    People reached through emergency response in 2025
                  </p>
                </div>
                <div className="h-px bg-border" />
                <div className="text-center">
                  <div className="text-5xl font-headline font-bold text-success mb-2">
                    94%
                  </div>
                  <p className="text-sm text-muted-foreground font-body">
                    Of emergency funds deployed within 72 hours
                  </p>
                </div>
              </div>
              <button className="w-full mt-8 px-6 py-4 bg-primary text-primary-foreground rounded-lg font-cta font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg flex items-center justify-center space-x-2">
                <Icon name="BoltIcon" size={24} variant="solid" />
                <span>Support Emergency Fund</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <EmergencyFundModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProject(null);
        }}
        project={selectedProject} />
      
    </section>);

};

export default EmergencyResponseSection;